import React, { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import Back from "~/components/Icons/arrow-prev2";
import { IsAdminRoleCluster } from "~/entity/Employee/model/staffRoleClusters";
import { checkRole } from "~/hooks/checkRole";
import { getFormatDate } from "~/libs/getFormatDate";
import { goBackToBaseStaff } from "~/pages/BaseStaff/lib/goBackToBaseStaff";
import { usePermissions } from "~/shared/context/PermissionContext";

import UserProfileModals from "./components/UserProfileModals";
import ParametersViewSection from "./sections/ParametersViewSection";
import PersonalDataViewSection from "./sections/PersonalDataViewSection";
import PositionHistorySection from "./sections/PositionHistorySection";
import PositionsViewSection from "./sections/PositionsViewSection";
import { DataRow, DataRows } from "./components/DataRows";
import { useUserProfileNavigation } from "./hooks/useUserProfileNavigation";
import { formatDisplayValue } from "./model/display";
import { TEmployeePosition, TEmployeeView } from "./model/types";

import s from "./EmployeeProfileView.less";

interface IEmployeeProfileViewProps {
  employee: TEmployeeView;
  employeeId: string;
  positions: TEmployeePosition[];
  hasFiredPosition: boolean;
  hasActivePosition: boolean;
  refetch: () => Promise<{ isError: boolean }>;
}

const TEMPORARY_ABSENCE_ALLOWED_STATUSES = [
  "unverified",
  "sendCheck",
  "verified",
  "rejected",
  "temporaryUnavailable",
];

const EmployeeProfileView = ({
  employee,
  employeeId,
  positions,
  hasFiredPosition,
  hasActivePosition,
  refetch,
}: IEmployeeProfileViewProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { openModal, closeModal, openCreatedBy, openLastEditBy } =
    useUserProfileNavigation();
  const { hasPermission } = usePermissions();
  const [restoreOpen, setRestoreOpen] = useState(false);
  const role = checkRole();
  const canRestore =
    IsAdminRoleCluster.includes(role as never) && hasFiredPosition;
  const canManageTemporaryAbsence =
    hasActivePosition &&
    TEMPORARY_ABSENCE_ALLOWED_STATUSES.indexOf(employee.status || "") !== -1 &&
    hasPermission("employeeAccounting.employee.status.change");

  return (
    <div className={s.wrapper}>
      <div className={s.pageTop}>
        <button
          type="button"
          className={s.backButton}
          onClick={() => goBackToBaseStaff(history)}
        >
          <Back width="7" height="11" />
          <span>{t("ui.back")}</span>
        </button>
        <div className={s.titleRow}>
          <h1 className={s.title}>{t("employee.titleEmploy")}</h1>
          {/* TODO PASDILER-2428: восстановление сотрудника временно скрыто до завершения работ в feat/PASDILER-1829. https://task.sotl.group:8443/browse/PASDILER-2428 */}
          {/* TODO PASDILER-2428: установка временного отсутствия временно скрыта до завершения работ в feat/PASDILER-1829. https://task.sotl.group:8443/browse/PASDILER-2428
          {(canRestore || canManageTemporaryAbsence) && (
            <div className={s.titleActions}>
              {canManageTemporaryAbsence && (
                <TemporaryAbsenceAction
                  key={employeeId}
                  employeeId={employeeId}
                  temporaryUnavailable={
                    employee.status === "temporaryUnavailable"
                  }
                  onSuccess={refetch}
                />
              )}
              {canRestore && (
                <Button
                  bold
                  color="primary"
                  fontSize="14"
                  size="medium"
                  radius={5}
                  onClick={() => setRestoreOpen(true)}
                >
                  {t("employee.restoreEmployee")}
                </Button>
              )}
            </div>
          )} */}
        </div>
      </div>

      <PersonalDataViewSection employee={employee} />

      <hr className={s.sectionDivider} />

      <ParametersViewSection
        employee={employee}
        createdByUser={employee.CreatedByUser}
        lastEditByUser={employee.LastEditByUser}
        onOpenCreatedBy={openCreatedBy}
        onOpenLastEditBy={openLastEditBy}
      />

      <hr className={s.sectionDivider} />

      <section className={s.sectionCompact}>
        <div className={s.laborHeader}>
          <h2 className={s.sectionTitle}>
            {t("employee.listTitleLaborActivity")}
          </h2>
          <DataRows>
            <DataRow label={`${t("employee.HiredDate")}:`}>
              {employee.HiredDate
                ? getFormatDate(String(employee.HiredDate), true)
                : formatDisplayValue(null)}
            </DataRow>
          </DataRows>
        </div>

        <PositionsViewSection
          employeeId={employeeId}
          positions={positions}
          refetch={refetch}
        />
      </section>

      <hr className={s.sectionDivider} />

      <PositionHistorySection positions={positions} />

      <UserProfileModals
        openModal={openModal}
        createdByUser={employee.CreatedByUser}
        lastEditByUser={employee.LastEditByUser}
        onClose={closeModal}
      />

      {/* TODO PASDILER-2428: модалка восстановления сотрудника временно скрыта до завершения работ в feat/PASDILER-1829. https://task.sotl.group:8443/browse/PASDILER-2428
      {restoreOpen && (
        <RestoreEmployeeModal
          employeeId={employeeId}
          onClose={() => setRestoreOpen(false)}
          onSuccess={refetch}
        />
      )} */}
    </div>
  );
};

export default memo(EmployeeProfileView);
